// Marketing: spend against what it won, from the lead funnel (signed language).
// Cost per won job is the number that matters, and it is computed here as
// cost ÷ won so the table always reconciles with itself. Spend entry is a
// proposal (record_spend); who may enter it is an open gate, so the form is
// plain. Money is gold and mono, and staff-only.
import { sydneyDay, sydneyToday, shiftSydneyDay } from "@artemis-group/aiw-contracts";
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Input, KindChip, caseKind, cn } from "@aiw/ui";
import { Page, Panel, Tile } from "../shell/Page";
import { useFunnel } from "../hooks/useReads";
import { OUTCOME_TONE, useCampaigns, useProposer, useWebsiteProducts } from "../hooks/slice-b";
import { aud, sourceLabel } from "../lib/format";
import { WebsiteTraffic } from "../components/website-traffic";

interface FunnelRow { source_id: string; channel: string; facet: string | null; month: string; leads: number; contacted: number; quoted: number; won: number; completed: number; lost: number; unworked: number; cost_aud: number | null; won_value_aud: number | null }
interface ChannelRow { channel: string; leads: number; contacted: number; quoted: number; won: number; completed: number; lost: number; unworked: number; cost: number; wonValue: number; costPerWon: number | null }

const SOURCES = ["hipages", "website", "referral", "trade_network", "sourced", "profit_share", "none"];
const BASES: { v: string; label: string }[] = [{ v: "invoice", label: "Invoice" }, { v: "statement", label: "Statement" }, { v: "credits", label: "Credits (rate card)" }, { v: "estimate", label: "Estimate · marked as one" }];
const STATE_TONE: Record<string, string> = { Live: "bg-success/15 text-success", "Decision open": "bg-warning/15 text-warning", Planned: "bg-accent text-accent-foreground" };

const Money = ({ n, strong }: { n: number | null; strong?: boolean }) => <span className={cn("num text-gold", strong && "font-semibold")}>{n == null ? "—" : aud(n)}</span>;

export function Marketing() {
  const funnel = useFunnel();
  const campaigns = useCampaigns();
  const products = useWebsiteProducts();
  const { send, outcomes, busy } = useProposer();
  const [spendOpen, setSpendOpen] = useState(false);

  const rows = useMemo<ChannelRow[]>(() => {
    const by = new Map<string, ChannelRow>();
    for (const r of (funnel.data ?? []) as unknown as FunnelRow[]) {
      const c = by.get(r.channel) ?? { channel: r.channel, leads: 0, contacted: 0, quoted: 0, won: 0, completed: 0, lost: 0, unworked: 0, cost: 0, wonValue: 0, costPerWon: null };
      c.leads += r.leads; c.contacted += r.contacted; c.quoted += r.quoted; c.won += r.won; c.completed += r.completed; c.lost += r.lost; c.unworked += r.unworked;
      c.cost += r.cost_aud ?? 0; c.wonValue += r.won_value_aud ?? 0;
      by.set(r.channel, c);
    }
    return [...by.values()].map((c) => ({ ...c, costPerWon: c.won > 0 ? c.cost / c.won : null })).sort((a, b) => b.leads - a.leads);
  }, [funnel.data]);
  const all = rows.reduce((t, c) => ({ leads: t.leads + c.leads, contacted: t.contacted + c.contacted, quoted: t.quoted + c.quoted, won: t.won + c.won, completed: t.completed + c.completed, lost: t.lost + c.lost, unworked: t.unworked + c.unworked, cost: t.cost + c.cost, wonValue: t.wonValue + c.wonValue }), { leads: 0, contacted: 0, quoted: 0, won: 0, completed: 0, lost: 0, unworked: 0, cost: 0, wonValue: 0 });
  const allCpw = all.won > 0 ? all.cost / all.won : null;
  const months = [...new Set(((funnel.data ?? []) as unknown as FunnelRow[]).map((r) => r.month))].sort();
  const monthWords = months.length ? // crm.v_lead_funnel.month is a full date (2026-09-01); appending "-01" again made every heading "Invalid Date".
    months.map((m) => new Date(`${String(m).slice(0, 7)}-15T12:00:00Z`).toLocaleDateString("en-AU", { month: "short", year: "numeric", timeZone: "Australia/Sydney" })).join(" – ") : "no months yet";
  const leadsWords = rows.slice(0, 2).map((c) => `${c.leads} ${sourceLabel(c.channel).toLowerCase()}`).join(" · ");

  return (
    <Page
      kicker="Grow"
      title="Marketing"
      lede="Spend against what it won, from the lead funnel: source · channel · facet · month. Cost per won job is the number that matters."
      actions={
        <>
          <span className="num text-micro text-muted-foreground">{monthWords}</span>
          <Button size="sm" variant={spendOpen ? "secondary" : "outline"} onClick={() => setSpendOpen((o) => !o)}><Plus /> Spend entry</Button>
        </>
      }
    >
      {spendOpen && (
        <Panel title="Spend entry" meta="who may enter spend is an open gate · until then this form is Jacky's" className="mb-4">
          <SpendEntry busy={!!busy.spend} outcome={outcomes.spend} onSubmit={(args) => send("spend", "record_spend", args, "Recorded · cost per won job updates on the next read")} />
        </Panel>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile n={<Money n={all.cost} />} label="Cost" sub={`${monthWords} · all sources`} />
        <Tile n={all.leads} label="Leads" sub={leadsWords || "none yet"} />
        <Tile n={all.won} label="Won" sub="from our stage, not the channel's “hired”" />
        <Tile n={<Money n={allCpw} />} label="Cost per won job" sub={allCpw != null ? `${aud(all.cost)} ÷ ${all.won}` : "nothing won yet"} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Panel title="Spend vs outcome · by channel" meta={<><span>{monthWords} rolled up</span> <span className="ml-2 rounded-full border border-gold/40 bg-gold/10 px-2 text-gold">$ staff only</span></>} className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
                  <tr>
                    <th className="pb-1 font-medium">Source</th>
                    <th className="pb-1 text-right font-medium">Leads</th>
                    <th className="hidden pb-1 text-right font-medium md:table-cell">Contacted</th>
                    <th className="hidden pb-1 text-right font-medium md:table-cell">Quoted</th>
                    <th className="pb-1 text-right font-medium">Won</th>
                    <th className="hidden pb-1 text-right font-medium lg:table-cell">Lost</th>
                    <th className="hidden pb-1 text-right font-medium lg:table-cell">Unworked</th>
                    <th className="pb-1 text-right font-medium">Cost</th>
                    <th className="pb-1 text-right font-medium">Cost / won</th>
                    <th className="hidden pb-1 text-right font-medium md:table-cell">Won value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.channel} className="border-t">
                      <td className="py-2 pr-2"><div className="font-medium">{sourceLabel(c.channel)}</div><div className="text-micro text-muted-foreground">{c.channel}</div></td>
                      <td className="num py-2 text-right">{c.leads}</td>
                      <td className="num hidden py-2 text-right md:table-cell">{c.contacted}</td>
                      <td className="num hidden py-2 text-right md:table-cell">{c.quoted}</td>
                      <td className="num py-2 text-right">{c.won}</td>
                      <td className="num hidden py-2 text-right text-muted-foreground lg:table-cell">{c.lost}</td>
                      <td className="num hidden py-2 text-right text-muted-foreground lg:table-cell">{c.unworked}</td>
                      <td className="py-2 text-right"><Money n={c.cost} /></td>
                      <td className="py-2 text-right"><Money n={c.costPerWon} /></td>
                      <td className="hidden py-2 text-right md:table-cell"><Money n={c.wonValue} /></td>
                    </tr>
                  ))}
                  {rows.length > 0 && (
                    <tr className="border-t font-semibold">
                      <td className="py-2 pr-2">All</td>
                      <td className="num py-2 text-right">{all.leads}</td>
                      <td className="num hidden py-2 text-right md:table-cell">{all.contacted}</td>
                      <td className="num hidden py-2 text-right md:table-cell">{all.quoted}</td>
                      <td className="num py-2 text-right">{all.won}</td>
                      <td className="num hidden py-2 text-right lg:table-cell">{all.lost}</td>
                      <td className="num hidden py-2 text-right lg:table-cell">{all.unworked}</td>
                      <td className="py-2 text-right"><Money n={all.cost} strong /></td>
                      <td className="py-2 text-right"><Money n={allCpw} strong /></td>
                      <td className="hidden py-2 text-right md:table-cell"><Money n={all.wonValue} strong /></td>
                    </tr>
                  )}
                  {rows.length === 0 && <tr><td colSpan={10} className="py-4 text-center text-muted-foreground">No funnel rows yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-micro text-muted-foreground">Cost per won job is cost ÷ won, computed on this page. Won means our stage moved, not that the channel said “hired”.</p>
          </Panel>

          <WebsiteTraffic />

          <Panel title="Website product performance" meta="views → enquiries → quotes · last 30 days">
            {(products.data ?? []).length === 0 ? <p className="text-sm text-muted-foreground">No product views recorded yet.</p> : (
              <ul className="space-y-1.5 text-sm">
                {(products.data ?? []).map((p, _i, arr) => {
                  const max = arr[0]?.views ?? 1;
                  return (
                    <li key={p.product} className="grid grid-cols-[minmax(0,12rem)_1fr_auto] items-center gap-3">
                      <span className="truncate">{p.product}</span>
                      <span className="h-2 rounded-sm bg-muted"><span className="block h-2 rounded-sm bg-primary" style={{ width: `${Math.round((p.views / max) * 100)}%` }} /></span>
                      <span className="num text-micro text-muted-foreground">{p.views.toLocaleString("en-AU")} · {p.enquiries} · {p.quotes}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Won value · by channel" meta={monthWords}>
            {rows.length === 0 ? <p className="text-sm text-muted-foreground">Nothing won yet.</p> : <WonValueBars rows={rows} />}
          </Panel>

          <Panel title="Campaigns" meta="channel · budget · period" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
                  <tr><th className="pb-1 font-medium">Campaign</th><th className="pb-1 font-medium">Budget</th><th className="pb-1 font-medium">Period</th><th className="pb-1 font-medium"></th></tr>
                </thead>
                <tbody>
                  {(campaigns.data ?? []).map((c) => (
                    <tr key={c.id} className="border-t align-top">
                      <td className="py-2 pr-2"><div className="font-medium">{c.name}</div><div className="text-micro text-muted-foreground">{sourceLabel(c.source_id)} · {c.channel}</div></td>
                      <td className="py-2 pr-2 text-micro"><span className={/\$/.test(c.budget) ? "num text-gold" : ""}>{c.budget}</span></td>
                      <td className="num py-2 pr-2 text-micro text-muted-foreground">{c.period}</td>
                      <td className="py-2 text-right"><span className={cn("rounded-full px-2 text-micro", STATE_TONE[c.state] ?? "bg-muted text-muted-foreground")}>{c.state}</span></td>
                    </tr>
                  ))}
                  {(campaigns.data ?? []).length === 0 && <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No campaigns yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    </Page>
  );
}

/** One series (money → gold), horizontal so the channel names read; values in ink. */
function WonValueBars({ rows }: { rows: ChannelRow[] }) {
  const data = rows.filter((r) => r.wonValue > 0).sort((a, b) => b.wonValue - a.wonValue);
  const max = Math.max(1, ...data.map((d) => d.wonValue));
  const labelW = 110, valueW = 80, w = 360, rowH = 26, barH = 14;
  const plotW = w - labelW - valueW;
  return (
    <svg viewBox={`0 0 ${w} ${data.length * rowH}`} className="h-auto w-full" role="img" aria-label="Won value by channel">
      {data.map((d, i) => {
        const bw = Math.max(4, Math.round((d.wonValue / max) * plotW));
        const y = i * rowH;
        return (
          <g key={d.channel}>
            <title>{`${sourceLabel(d.channel)} · ${aud(d.wonValue)} won from ${d.won} job${d.won === 1 ? "" : "s"}`}</title>
            <text x={labelW - 8} y={y + rowH / 2 + 4} textAnchor="end" fontSize={13} className="fill-foreground">{sourceLabel(d.channel)}</text>
            <rect x={labelW} y={y + (rowH - barH) / 2} width={bw} height={barH} rx={4} className="fill-gold" />
            <text x={labelW + bw + 6} y={y + rowH / 2 + 4} fontSize={13} className="fill-foreground font-mono">{aud(d.wonValue)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function SpendEntry({ busy, outcome, onSubmit }: { busy: boolean; outcome?: { tone: "ok" | "wait" | "no"; text: string }; onSubmit: (args: Record<string, unknown>) => void }) {
  // This Sydney month, first to last day — not the device's month.
  const monthStart = `${sydneyToday().slice(0, 8)}01`;
  const monthEnd = shiftSydneyDay(`${shiftSydneyDay(monthStart, 32).slice(0, 8)}01`, -1);
  const [source, setSource] = useState("website");
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(monthEnd);
  const [amount, setAmount] = useState("600");
  const [basis, setBasis] = useState("invoice");
  const [note, setNote] = useState("");
  const amountN = Number(amount);
  const valid = source && start && end && end >= start && Number.isFinite(amountN) && amountN >= 0;
  const field = "grid gap-1 text-sm";
  const label = "text-micro text-muted-foreground";
  const select = "h-9 rounded-md border bg-background px-2 text-sm";
  return (
    <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit({ source_id: source, campaign_id: null, period_start: start, period_end: end, amount_aud: amountN, basis, note: note.trim() || null }); }}>
      <label className={field}><span className={label}>Source</span><select className={select} value={source} onChange={(e) => setSource(e.target.value)}>{SOURCES.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}</select></label>
      <label className={field}><span className={label}>Period from</span><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
      <label className={field}><span className={label}>Period to</span><Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} /></label>
      <label className={field}><span className={label}>Amount (AUD, ex GST)</span><Input type="number" min={0} step={1} inputMode="decimal" className="num text-gold" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
      <label className={field}><span className={label}>Cost basis</span><select className={select} value={basis} onChange={(e) => setBasis(e.target.value)}>{BASES.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}</select></label>
      <label className={field}><span className={label}>Note</span><Input value={note} maxLength={500} placeholder="e.g. Google Ads September invoice" onChange={(e) => setNote(e.target.value)} /></label>
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-3">
        <Button type="submit" size="sm" disabled={busy || !valid}>Save spend</Button>
        <KindChip kind={caseKind({ command: "record_spend", radius: 2 })} />
        {outcome && <span className={cn("text-micro", OUTCOME_TONE[outcome.tone])}>{outcome.text}</span>}
        <span className="ml-auto text-micro text-muted-foreground">An estimate is marked as one; the funnel shows it that way.</span>
      </div>
    </form>
  );
}
